// src/components/interactions/configs/allInteractionTypes.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 

  Heart,
  BookOpen,
  Printer,
  UserPlus,
  AtSign,
  Mail,
  MessageSquare,
  Bell,
  Megaphone,
  ClipboardList,
  FileText,
  CheckCircle,
  User,
  BarChart3,
  LogIn,
  Shield,
  Key,
  Brain,
  Upload,
  TrendingUp,
  ClipboardCheck,
  Calendar,
  Activity,
  Download, 
  Share2, 
  ZoomIn, 
  Play, 
  Grid, 
  List,
  Filter,
  Search,
  ArrowLeft,
  Plus,
  Eye,
  MessageCircle,
  Bookmark,
  MoreHorizontal,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Users,
  Lock,
  Globe
} from 'lucide-react';


export const INTERACTION_TYPES = {
  
  // ==================== CONTENT INTERACTIONS ====================
  contentInteractions: {
    viewing: {
      label: 'Content Viewing',
      description: 'Track and manage content views',
      icon: Eye,
      features: ['viewTracking', 'analytics', 'popularContent'],
      settings: ['trackAnonymous', 'showViewCounts', 'viewHistory']
    },
    reading: {
      label: 'Reading Progress',
      description: 'Track reading progress and bookmarks',
      icon: BookOpen,
      features: ['progressTracking', 'bookmarks', 'readingTime'],
      settings: ['saveProgress', 'estimateTime', 'readingGoals']
    },
    downloading: {
      label: 'File Downloads',
      description: 'Enable file and document downloads',
      icon: Download,
      features: ['fileAccess', 'downloadTracking', 'accessControl'],
      settings: ['requireAuth', 'trackDownloads', 'downloadLimits']
    },
    printing: {
      label: 'Print Content',
      description: 'Allow users to print content',
      icon: Printer,
      features: ['printOptimization', 'printTracking'],
      settings: ['printFriendly', 'trackPrints', 'printPermissions']
    },
    searching: {
      label: 'Content Search',
      description: 'Enable content search functionality',
      icon: Search,
      features: ['fullTextSearch', 'filtering', 'searchAnalytics'],
      settings: ['searchScope', 'searchTracking', 'searchSuggestions']
    }
  },

  // ==================== SOCIAL INTERACTIONS ====================
  socialInteractions: {
    liking: {
      label: 'Likes & Reactions',
      description: 'Users can like and react to content',
      icon: Heart,
      features: ['reactions', 'likeTracking', 'reactionTypes'],
      settings: ['allowAnonymous', 'reactionOptions', 'showCounts']
    },
    commenting: {
      label: 'Comments & Discussions',
      description: 'Enable commenting and discussions',
      icon: MessageCircle,
      features: ['threading', 'moderation', 'mentions'],
      settings: ['moderation', 'allowAnonymous', 'threading', 'notifications']
    },
    sharing: {
      label: 'Social Sharing',
      description: 'Share content on social platforms',
      icon: Share2,
      features: ['socialPlatforms', 'shareTracking', 'customMessages'],
      settings: ['platforms', 'trackShares', 'shareButtons']
    },
    following: {
      label: 'Following & Subscriptions',
      description: 'Users can follow for updates',
      icon: UserPlus,
      features: ['followers', 'notifications', 'updates'],
      settings: ['followPermissions', 'notificationTypes', 'followerVisibility']
    },
    mentions: {
      label: 'User Mentions',
      description: 'Mention and tag other users',
      icon: AtSign,
      features: ['userTagging', 'mentionNotifications'],
      settings: ['mentionPermissions', 'notifyMentions', 'searchUsers']
    },
    forums: {
      label: 'Discussion Forums',
      description: 'Create community forums and discussions',
      icon: Users,
      features: ['topics', 'categories', 'moderation'],
      settings: ['forumStructure', 'moderationRules', 'userRoles']
    }
  },

  // ==================== COMMUNICATION INTERACTIONS ====================
  communicationInteractions: {
    messaging: {
      label: 'Direct Messaging',
      description: 'Private messages between users',
      icon: Mail,
      features: ['privateMessages', 'groupMessages', 'messageHistory'],
      settings: ['messagePermissions', 'messageRetention', 'messageModeration']
    },
    chat: {
      label: 'Live Chat',
      description: 'Real-time chat functionality',
      icon: MessageSquare,
      features: ['realTimeChat', 'chatRooms', 'chatHistory'],
      settings: ['chatPermissions', 'chatModeration', 'chatArchiving']
    },
    notifications: {
      label: 'Push Notifications',
      description: 'Send notifications to users',
      icon: Bell,
      features: ['pushNotifications', 'emailNotifications', 'smsNotifications'],
      settings: ['notificationTypes', 'frequency', 'channels']
    },
    announcements: {
      label: 'Announcements',
      description: 'Broadcast announcements to users',
      icon: Megaphone,
      features: ['broadcasts', 'targeting', 'scheduling'],
      settings: ['audienceTargeting', 'scheduledAnnouncements', 'announcementTypes']
    },
    feedback: {
      label: 'Feedback & Suggestions',
      description: 'Collect user feedback and suggestions',
      icon: MessageCircle,
      features: ['feedbackForms', 'suggestion', 'voting'],
      settings: ['feedbackTypes', 'moderation', 'responseTracking']
    }
  },

  // ==================== DATA COLLECTION INTERACTIONS ====================
  dataCollectionInteractions: {
    surveys: {
      label: 'Surveys & Polls',
      description: 'Create surveys and polls for users',
      icon: ClipboardList,
      features: ['multipleChoice', 'openEnded', 'analytics'],
      settings: ['surveyTypes', 'responseAnalytics', 'anonymousResponses']
    },
    forms: {
      label: 'Custom Forms',
      description: 'Create custom data collection forms',
      icon: FileText,
      features: ['formBuilder', 'validation', 'submissions'],
      settings: ['fieldTypes', 'validation', 'submissionHandling']
    },
    registration: {
      label: 'User Registration',
      description: 'User account creation and registration',
      icon: UserPlus,
      features: ['userAccounts', 'profiles', 'verification'],
      settings: ['registrationFields', 'verification', 'approvalProcess']
    },
    profiling: {
      label: 'User Profiling',
      description: 'Collect and manage user profiles',
      icon: User,
      features: ['profileCustomization', 'preferences', 'demographics'],
      settings: ['profileFields', 'privacy', 'profileVisibility']
    },
    analytics: {
      label: 'User Analytics',
      description: 'Track user behavior and analytics',
      icon: BarChart3,
      features: ['behaviorTracking', 'heatmaps', 'conversionTracking'],
      settings: ['trackingScope', 'dataRetention', 'privacyCompliance']
    }
  },

  // ==================== AUTHENTICATION & ACCESS INTERACTIONS ====================
  authenticationInteractions: {
    login: {
      label: 'User Login/Logout',
      description: 'User authentication system',
      icon: LogIn,
      features: ['multipleAuthMethods', 'sessionManagement', 'securityFeatures'],
      settings: ['authMethods', 'sessionTimeout', 'securityPolicies']
    },
    verification: {
      label: 'Account Verification',
      description: 'Verify user accounts and identity',
      icon: Shield,
      features: ['emailVerification', 'phoneVerification', 'documentVerification'],
      settings: ['verificationMethods', 'verificationRequirements']
    },
    permissions: {
      label: 'Access Control',
      description: 'Manage user permissions and access',
      icon: Lock,
      features: ['roleBasedAccess', 'permissionLevels', 'accessLogs'],
      settings: ['roleDefinitions', 'permissionMatrix', 'accessLogging']
    },
    passwordReset: {
      label: 'Password Management',
      description: 'Password reset and management',
      icon: Key,
      features: ['passwordReset', 'passwordPolicies', 'securityQuestions'],
      settings: ['passwordRequirements', 'resetMethods', 'securityPolicies']
    }
  },

  // ==================== LEARNING & EDUCATIONAL INTERACTIONS ====================
  learningInteractions: {
    quizzes: {
      label: 'Quizzes & Assessments',
      description: 'Interactive quizzes and assessments',
      icon: Brain,
      features: ['multipleChoice', 'openEnded', 'timedQuizzes', 'grading'],
      settings: ['questionTypes', 'grading', 'timeLimit', 'attempts']
    },
    assignments: {
      label: 'Assignment Submission',
      description: 'Submit and manage assignments',
      icon: Upload,
      features: ['fileSubmission', 'deadlines', 'grading', 'feedback'],
      settings: ['submissionTypes', 'deadlineEnforcement', 'gradingRubrics']
    },
    progress: {
      label: 'Learning Progress',
      description: 'Track learning progress and achievements',
      icon: TrendingUp,
      features: ['progressTracking', 'achievements', 'certificates'],
      settings: ['progressMetrics', 'achievementCriteria', 'certificationRequirements']
    },
    collaboration: {
      label: 'Study Groups',
      description: 'Collaborative learning features',
      icon: Users,
      features: ['studyGroups', 'sharedNotes', 'groupProjects'],
      settings: ['groupFormation', 'collaborationTools', 'groupManagement']
    }
  },

  // ==================== WORKFLOW & PROCESS INTERACTIONS ====================
  workflowInteractions: {
    approvals: {
      label: 'Approval Workflows',
      description: 'Manage approval processes',
      icon: CheckCircle,
      features: ['multiStepApproval', 'approvalHistory', 'notifications'],
      settings: ['approvalChain', 'approvalCriteria', 'escalationRules']
    },
    tasks: {
      label: 'Task Management',
      description: 'Assign and manage tasks',
      icon: ClipboardCheck,
      features: ['taskAssignment', 'deadlines', 'tracking'],
      settings: ['taskTypes', 'assignmentRules', 'trackingMethods']
    },
    scheduling: {
      label: 'Scheduling & Calendar',
      description: 'Schedule appointments and events',
      icon: Calendar,
      features: ['calendar', 'appointments', 'availability'],
      settings: ['scheduleRules', 'availability', 'conflictResolution']
    },
    status: {
      label: 'Status Updates',
      description: 'Track and update status',
      icon: Activity,
      features: ['statusTracking', 'statusHistory', 'notifications'],
      settings: ['statusDefinitions', 'updatePermissions', 'statusNotifications']
    }
  }
};